import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import LoadingIcon from "src/Components/Common/LoadingIcon";
import useFetch from "src/Components/Common/useFetch";
import { userContext } from "src/Components/Contexts/userContext";
import { UserRole, UserTypes } from "src/entity/models/FrontendActor";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";

// Values must match the backend enums (Department / Category / ItemType in MergedReport.types)
const DEPARTMENT_OPTIONS = [
  { value: "Finance", label: "Finance" },
  { value: "Inwarding", label: "Warehouse (Inwarding)" },
  { value: "Procurement", label: "Procurement" },
  { value: "AR", label: "AR" },
  { value: "Category", label: "Category" },
  { value: "Zoho Team", label: "Zoho Team" },
];
const CATEGORY_OPTIONS = ["Both", "DSD", "Non DSD"];
const ITEM_TYPE_OPTIONS = ["Both", "FnV", "Non FnV"];

interface DrUserProfileRow {
  userId: number;
  name: string;
  email: string;
  role: UserRole;
  actorId: number;
  department: string | null;
  category: string | null;
  itemType: string | null;
  updatedAt: string | null;
}

type ProfileDraft = Pick<DrUserProfileRow, "department" | "category" | "itemType">;

/** Who may change DR user settings; must match CommonCoreDR.updateDrUserProfile on the server. */
export const canManageDrUserSettings = (actor: { userType?: UserTypes; userRole?: UserRole }) =>
  actor?.userType === UserTypes.Admin ||
  actor?.userType === UserTypes.CustomerSuccess ||
  actor?.userRole === UserRole.ReconDRApprover;

const DrUserSettings = () => {
  const { actor } = useContext(userContext);
  const canEdit = canManageDrUserSettings(actor);

  const [users, setUsers] = useState<DrUserProfileRow[]>([]);
  const [drafts, setDrafts] = useState<Record<number, ProfileDraft>>({});
  const [savingUserId, setSavingUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchUsers = () => {
    setLoading(true);
    useFetch<{ data: DrUserProfileRow[] }>(API_ENDPOINTS.FETCH_DR_USER_PROFILES.url, "GET", {
      failureMessage: API_ENDPOINTS.FETCH_DR_USER_PROFILES.failureMessage,
      thenCallBack: (response) => {
        setUsers(response.data.data || []);
        setDrafts({});
        setLoading(false);
      },
      catchCallBack: () => setLoading(false),
    });
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter((user) => {
      if (onlyMissing && user.department) return false;
      if (!term) return true;
      return user.name?.toLowerCase().includes(term) || user.email?.toLowerCase().includes(term);
    });
  }, [users, search, onlyMissing]);

  const missingCount = useMemo(() => users.filter((user) => !user.department).length, [users]);

  const valueOf = (user: DrUserProfileRow): ProfileDraft => drafts[user.userId] ?? user;

  const setDraftField = (user: DrUserProfileRow, field: keyof ProfileDraft, value: string) => {
    setDrafts((prev) => ({ ...prev, [user.userId]: { ...valueOf(user), [field]: value } }));
  };

  const isDirty = (user: DrUserProfileRow) => {
    const draft = drafts[user.userId];
    return (
      !!draft &&
      (draft.department !== user.department || draft.category !== user.category || draft.itemType !== user.itemType)
    );
  };

  const saveUser = (user: DrUserProfileRow) => {
    const draft = valueOf(user);
    setSavingUserId(user.userId);
    useFetch(API_ENDPOINTS.UPDATE_DR_USER_PROFILE(user.userId).url, "PUT", {
      data: { department: draft.department, category: draft.category, itemType: draft.itemType },
      failureMessage: API_ENDPOINTS.UPDATE_DR_USER_PROFILE(user.userId).failureMessage,
      showSuccessToast: true,
      thenCallBack: () => {
        setUsers((prev) => prev.map((row) => (row.userId === user.userId ? { ...row, ...draft } : row)));
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[user.userId];
          return next;
        });
        setSavingUserId(null);
      },
      catchCallBack: () => setSavingUserId(null),
    });
  };

  const renderSelect = (
    user: DrUserProfileRow,
    field: keyof ProfileDraft,
    options: { value: string; label: string }[]
  ) => (
    <Select
      size="small"
      fullWidth
      displayEmpty
      disabled={!canEdit}
      value={valueOf(user)[field] ?? ""}
      onChange={(e) => setDraftField(user, field, e.target.value)}
      renderValue={(selected) =>
        selected ? (
          options.find((option) => option.value === selected)?.label ?? selected
        ) : (
          <Typography variant="body2" color="error">
            Not set
          </Typography>
        )
      }
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );

  const toOptions = (values: string[]) => values.map((value) => ({ value, label: value }));

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ p: 3, minWidth: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h6">DR User Settings</Typography>
            <Typography variant="body2" color="text.secondary">
              Department, DSD category and item type decide which rejected invoices and disputed entries each DR user
              works on.
              {!canEdit && " Only Recon DR approvers, Admin or Customer Success can change these settings."}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyMissing}
                  onChange={(e) => {
                    setOnlyMissing(e.target.checked);
                    setPage(0);
                  }}
                />
              }
              label={`Only users without a department (${missingCount})`}
            />
            <TextField
              size="small"
              placeholder="Search name or email"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <LoadingIcon loading={true} />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxWidth: "100%", overflowX: "auto" }}>
            {/* Fixed min width so narrow screens scroll sideways instead of squeezing names */}
            <Table size="small" sx={{ minWidth: 1300, "& td, & th": { whiteSpace: "nowrap" } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 180 }}>Name</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Account ID</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Department</TableCell>
                  <TableCell sx={{ minWidth: 130 }}>Category</TableCell>
                  <TableCell sx={{ minWidth: 130 }}>Item Type</TableCell>
                  {canEdit && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => {
                  const draft = valueOf(user);
                  const complete = !!draft.department && !!draft.category && !!draft.itemType;
                  return (
                    <TableRow key={user.userId} hover>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip size="small" label={user.role} />
                      </TableCell>
                      <TableCell>{user.actorId}</TableCell>
                      <TableCell>{renderSelect(user, "department", DEPARTMENT_OPTIONS)}</TableCell>
                      <TableCell>{renderSelect(user, "category", toOptions(CATEGORY_OPTIONS))}</TableCell>
                      <TableCell>{renderSelect(user, "itemType", toOptions(ITEM_TYPE_OPTIONS))}</TableCell>
                      {canEdit && (
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={!isDirty(user) || !complete || savingUserId === user.userId}
                            onClick={() => saveUser(user)}
                          >
                            {savingUserId === user.userId ? "Saving..." : "Save"}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 8 : 7} align="center">
                      No DR users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredUsers.length}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </TableContainer>
        )}
      </Card>
    </Box>
  );
};

export default DrUserSettings;
